export type SanitizableOptionValue = {
  value?: unknown;
  label?: unknown;
  hidden?: boolean;
  active?: boolean;
  metadata?: unknown;
};

export type SanitizableProductOption = {
  id?: string;
  name?: string;
  values?: SanitizableOptionValue[];
  hidden?: boolean;
  active?: boolean;
};

function optionKey(option: SanitizableProductOption) {
  return String(option.id || option.name || '').toLowerCase();
}

export function isUsableProductOptionValue(option: SanitizableProductOption, value: SanitizableOptionValue) {
  if (value?.active === false || value?.hidden === true) return false;
  const metadata = value?.metadata;
  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    const meta = metadata as Record<string, unknown>;
    if (meta.hidden === true || meta.active === false) return false;
  }

  const rawValue = value?.value;
  if (rawValue === undefined || rawValue === null) return false;

  const normalized = String(rawValue).trim().toLowerCase();
  if (!normalized || normalized === 'null' || normalized === 'undefined' || normalized === 'nan') return false;

  if (['copies', 'quantity', 'amount'].includes(optionKey(option))) {
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return false;
  }

  return true;
}

export function sanitizeProductOptionValues<T extends SanitizableProductOption>(options: T[] = []): T[] {
  return options.map((option) => {
    if (!Array.isArray(option.values)) return option;
    return {
      ...option,
      values: option.values.filter((value) => isUsableProductOptionValue(option, value)),
    };
  });
}

export function sanitizeProductOptionsForPersistence<T extends SanitizableProductOption & { default?: unknown }>(options: T[] = []): T[] {
  return sanitizeProductOptionValues(options).map((option) => {
    if (!Array.isArray(option.values)) return option;

    const hasUsableDefault = option.default !== undefined
      && option.values.some((value) => String(value.value) === String(option.default));

    if (hasUsableDefault || option.default === undefined) return option;

    const fallbackDefault = option.values[0]?.value;
    if (fallbackDefault === undefined) {
      const { default: _default, ...optionWithoutDefault } = option;
      return optionWithoutDefault as T;
    }

    return {
      ...option,
      default: fallbackDefault,
    };
  });
}

export function sanitizeProductOptionsByLanguageForPersistence<T extends Record<string, SanitizableProductOption[] | unknown>>(optionsByLanguage: T): T {
  return Object.fromEntries(
    Object.entries(optionsByLanguage || {}).map(([language, options]) => [
      language,
      Array.isArray(options) ? sanitizeProductOptionsForPersistence(options) : options,
    ]),
  ) as T;
}
